import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter, ChevronRight, Grid, List, Search, X, Fuel, Zap, Flame } from 'lucide-react';
import AnimatedSection from '../components/common/AnimatedSection';
import SEOHead from '../components/common/SEOHead';
import { products, fuelTypes, brands } from '../data/mock';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const selectedFuelType = searchParams.get('fuel') || '';
  const selectedBrand = searchParams.get('brand') || '';

  // Get fuel type icon
  const getFuelIcon = (fuelId) => {
    switch (fuelId) {
      case 'diesel':
        return <Fuel size={20} />;
      case 'glp':
        return <Flame size={20} />;
      case 'eletrica':
        return <Zap size={20} />;
      default:
        return null;
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesFuelType = !selectedFuelType || product.fuelType === selectedFuelType;
      const matchesBrand = !selectedBrand || product.brand === selectedBrand;
      const matchesSearch = !searchQuery || 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.brand.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFuelType && matchesBrand && matchesSearch;
    });
  }, [selectedFuelType, selectedBrand, searchQuery]);

  const handleFilterChange = (type, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(type, value);
    } else {
      newParams.delete(type);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchQuery('');
  };

  const hasActiveFilters = selectedFuelType || selectedBrand || searchQuery;

  return (
    <>
      <SEOHead 
        title="Produtos - Empilhadeiras e Transpaletes"
        description="Conheça nossa linha completa de empilhadeiras diesel, GLP, elétricas e transpaletes. Marcas MAXIMAL, HANGCHA e PALETRANS."
        keywords="empilhadeiras venda, transpalete, empilhadeira diesel, empilhadeira elétrica, MAXIMAL, HANGCHA, PALETRANS"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900/30 py-20">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="products-page-title">Nossos Produtos</h1>
            <p className="text-xl text-gray-400">Empilhadeiras e equipamentos de alta qualidade</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Top Fuel Type Filters */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-30" data-testid="fuel-type-filters">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-2 py-4">
            <button
              onClick={() => handleFilterChange('fuel', '')}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                !selectedFuelType 
                  ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="filter-all"
            >
              Todos
            </button>
            {fuelTypes.map((fuel) => (
              <button
                key={fuel.id}
                onClick={() => handleFilterChange('fuel', fuel.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  selectedFuelType === fuel.id 
                    ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`filter-${fuel.id}`}
              >
                {getFuelIcon(fuel.id)}
                {fuel.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Sidebar - Brands Only */}
            <aside className={`lg:w-64 flex-shrink-0 ${
              showFilters ? 'fixed inset-0 z-50 bg-white p-6 overflow-auto lg:relative lg:bg-transparent lg:p-0' : 'hidden lg:block'
            }`}>
              <div className="lg:sticky lg:top-40">
                <div className="flex items-center justify-between mb-6 lg:hidden">
                  <h3 className="text-xl font-bold">Filtros</h3>
                  <button onClick={() => setShowFilters(false)} data-testid="close-filters-btn">
                    <X size={24} />
                  </button>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
                  {/* Search */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-3 block">Buscar</label>
                    <div className="relative">
                      <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Nome ou modelo..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                        data-testid="search-input"
                      />
                    </div>
                  </div>

                  {/* Brands */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-3 block">Marcas</label>
                    <div className="space-y-2">
                      <button
                        onClick={() => handleFilterChange('brand', '')}
                        className={`w-full text-left px-4 py-2.5 rounded-xl transition-all ${
                          !selectedBrand ? 'bg-teal-500 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                        data-testid="brand-filter-all"
                      >
                        Todas as Marcas
                      </button>
                      {brands.map((brand) => (
                        <button
                          key={brand.id}
                          onClick={() => handleFilterChange('brand', brand.name)}
                          className={`w-full text-left px-4 py-2.5 rounded-xl transition-all font-medium ${
                            selectedBrand === brand.name 
                              ? 'bg-teal-500 text-white' 
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                          }`}
                          data-testid={`brand-filter-${brand.name.toLowerCase()}`}
                        >
                          {brand.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Clear Filters */}
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="w-full py-2.5 text-teal-600 font-medium hover:bg-teal-50 rounded-xl transition-all border border-teal-200"
                      data-testid="clear-filters-btn"
                    >
                      Limpar Filtros
                    </button>
                  )}
                </div>
              </div>
            </aside>

            {/* Products Grid */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setShowFilters(true)}
                    className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-gray-200"
                    data-testid="mobile-filters-btn"
                  >
                    <Filter size={18} />
                    Filtros
                  </button>
                  <span className="text-gray-600" data-testid="products-count">
                    {filteredProducts.length} {filteredProducts.length === 1 ? 'produto encontrado' : 'produtos encontrados'}
                  </span>
                </div>
                <div className="hidden md:flex items-center gap-2 bg-white rounded-xl border border-gray-200 p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-teal-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                    data-testid="view-grid-btn"
                  >
                    <Grid size={18} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-teal-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                    data-testid="view-list-btn"
                  >
                    <List size={18} />
                  </button>
                </div>
              </div>

              {/* Active Filters Display */}
              {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="active-filters">
                  <span className="text-sm text-gray-500">Filtros ativos:</span>
                  {selectedFuelType && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                      {getFuelIcon(selectedFuelType)}
                      {fuelTypes.find(f => f.id === selectedFuelType)?.name}
                      <button onClick={() => handleFilterChange('fuel', '')} className="ml-1 hover:text-teal-900">
                        <X size={14} />
                      </button>
                    </span>
                  )}
                  {selectedBrand && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                      {selectedBrand}
                      <button onClick={() => handleFilterChange('brand', '')} className="ml-1 hover:text-teal-900">
                        <X size={14} />
                      </button>
                    </span>
                  )}
                  {searchQuery && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                      "{searchQuery}"
                      <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-teal-900">
                        <X size={14} />
                      </button>
                    </span>
                  )}
                </div>
              )}

              {/* Products */}
              {filteredProducts.length === 0 ? (
                <div className="text-center py-16" data-testid="no-products">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Search size={40} className="text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg mb-2">Nenhum produto encontrado.</p>
                  <p className="text-gray-400 text-sm mb-4">Tente ajustar os filtros ou a busca.</p>
                  <button 
                    onClick={clearFilters} 
                    className="text-teal-600 font-medium hover:underline"
                    data-testid="clear-filters-empty"
                  >
                    Limpar filtros
                  </button>
                </div>
              ) : (
                <div className={viewMode === 'grid' 
                  ? 'grid md:grid-cols-2 xl:grid-cols-3 gap-6' 
                  : 'space-y-4'
                } data-testid="products-grid">
                  {filteredProducts.map((product, index) => (
                    <AnimatedSection key={product.id} animation="fadeUp" delay={index * 50}>
                      {viewMode === 'grid' ? (
                        <Link 
                          to={`/produtos/${product.id}`}
                          className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-teal-200 transition-all duration-500"
                          data-testid={`product-card-${product.id}`}
                        >
                          <div className="relative overflow-hidden">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-full h-52 object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute top-4 left-4 flex gap-2">
                              <span className="bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                {product.brand}
                              </span>
                            </div>
                            {product.fuelType && (
                              <div className="absolute top-4 right-4">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                  product.fuelType === 'diesel' ? 'bg-orange-100 text-orange-700' :
                                  product.fuelType === 'glp' ? 'bg-blue-100 text-blue-700' :
                                  product.fuelType === 'eletrica' ? 'bg-green-100 text-green-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {getFuelIcon(product.fuelType)}
                                  {product.fuelType === 'diesel' ? 'Diesel' :
                                   product.fuelType === 'glp' ? 'GLP' :
                                   product.fuelType === 'eletrica' ? 'Elétrica' :
                                   product.fuelType === 'manual' ? 'Manual' : ''}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="p-6">
                            <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                              {product.name}
                            </h3>
                            <p className="text-sm text-gray-500 mb-3">Modelo: {product.model}</p>
                            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-teal-600 font-semibold">{product.capacity}</span>
                              <span className="text-teal-500 flex items-center gap-1 text-sm font-medium group-hover:translate-x-1 transition-transform">
                                Ver detalhes <ChevronRight size={16} />
                              </span>
                            </div>
                          </div>
                        </Link>
                      ) : (
                        <Link 
                          to={`/produtos/${product.id}`}
                          className="group flex bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-teal-200 transition-all duration-500"
                          data-testid={`product-card-list-${product.id}`}
                        >
                          <div className="w-48 flex-shrink-0 relative">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                            {product.fuelType && (
                              <div className="absolute bottom-2 left-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                  product.fuelType === 'diesel' ? 'bg-orange-100 text-orange-700' :
                                  product.fuelType === 'glp' ? 'bg-blue-100 text-blue-700' :
                                  product.fuelType === 'eletrica' ? 'bg-green-100 text-green-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {getFuelIcon(product.fuelType)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="p-6 flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <span className="bg-teal-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                                  {product.brand}
                                </span>
                                <h3 className="font-bold text-gray-900 mt-2 group-hover:text-teal-600 transition-colors">
                                  {product.name}
                                </h3>
                                <p className="text-sm text-gray-500">Modelo: {product.model}</p>
                              </div>
                              <span className="text-teal-600 font-semibold">{product.capacity}</span>
                            </div>
                            <p className="text-gray-600 text-sm mt-3">{product.description}</p>
                          </div>
                        </Link>
                      )}
                    </AnimatedSection>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductsPage;
